import React from "react";
import Styles from "./OnlineLoadBooking.module.scss";
import { useOlbAchievement, useOlbFeatures, useOlbHowItworkData, useLoadTypesData } from "../service-data/ServiceData";
import { FaqsResponse } from "@vahak/core/dist/_services/faq.service";
import { ga4sendEvent } from "@vahak/core/dist/_services/user-analytics.service";

import Features from "../common/features/Features";
import Achievements from "../common/achievements/Achievements";
import HeroSection from "../common/hero-section/HeroSection";
import HowItWorks from "../common/how-it-works/HowItWorks";
import Faq from "../common/faq/Faq";
import TypesOfLoads from "../types-of-loads/TypesOfLoads";
import { useTranslation } from "next-i18next";
import { GA4EventNames, GA4ServicePageOnlineLoadBookingEventValues } from "@vahak/core/dist/constants/event-names";
import { SOURCE_UTM } from "@vahak/core/dist/constants/misc";
import { PageTypes } from "@vahak/core/dist/constants/Pages";
import { ROUTES } from "@vahak/core/dist/constants/routeStrings";
import PageMetaTags from "../../common/page-meta-tags/PageMetaTags";
import { breadcrumbsConstants } from "../../../constants/breadcrumbs-constants";
import ServiceInformation from "../common/service-information/ServiceInformation";

const metaData = {
    title: "Online Load Booking For Trucks in India at 0% Commission | Vahak",
    description:
        "Make instant loads bookings and grow your transportation business with India's largest online marketplace, Vahak. Book now and join our #1 community today.",
    keywords:
        "load booking,online load booking,load for truck,truck load,book load,transport load,book truck loads online,online loads for trucks,online truck load booking,online truck loads,booking loads for trucks,find truck loads online,booking loads,load booking app,lorry load booking app,lorry load booking,online lorry load booking",
    canonicalUrl: "https://www.vahak.in/online-load-booking",
    schemaOrgJsonUrl: "/online-load-booking",
    ogImgSrc: "https://d3ib4ot6oar1ep.cloudfront.net/1920/75/webp/online-load-booking-vahak_desktop.png",
    ogImgAltText: "Online Load Booking",
    breadcrumbList: breadcrumbsConstants({}).onlineLoadBooking
};

interface OnlineLoadBookingProps {
    faqs: FaqsResponse[];
}

const OnlineLoadBooking = ({ faqs }: OnlineLoadBookingProps) => {
    const { t } = useTranslation("servicePages");

    return (
        <>
            <PageMetaTags
                title={metaData.title}
                description={metaData.description}
                keywords={metaData.keywords}
                canonicalUrl={metaData.canonicalUrl}
                ogUrl={metaData.canonicalUrl}
                ogImgSrc={metaData.ogImgSrc}
                ogImgAltText={metaData.ogImgAltText}
                schemaOrgJsonUrl={metaData.schemaOrgJsonUrl}
                breadcrumbList={metaData.breadcrumbList}
            />
            <main className={Styles.main}>
                <HeroSection
                    bannerTitle={t("servicePages:online-load-booking.hero-section.title")}
                    bannerText={t("servicePages:online-load-booking.hero-section.bannerText")}
                    hrefURL={`${ROUTES.marketplace.getUrl("load")}?${SOURCE_UTM}=${PageTypes.ONLINE_LOAD_BOOKING}`}
                    hrefLabel={t("servicePages:online-load-booking.hero-section.hrefLabel")}
                    onLinkClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_load_booking, {
                            value: GA4ServicePageOnlineLoadBookingEventValues.book_loads_now
                        });
                    }}
                    // srcSetURL={`${IMG_BASE_URL}/online-load-booking-vahak_desktop_3x.png 1473w, ${IMG_BASE_URL}/online-load-booking-vahak_desktop_2x.png 982w, ${IMG_BASE_URL}/online-load-booking-vahak_desktop.png 491w, ${IMG_BASE_URL}/online-load-booking-vahak_mobile_3x.png 828w, ${IMG_BASE_URL}/online-load-booking-vahak_mobile_2x.png 552w, ${IMG_BASE_URL}/online-load-booking-vahak_mobile.png 276w`}
                    srcURL="online-load-booking-vahak_desktop.png"
                    altText="a mobile phone with a truck and boxes"
                    imgboxClass={Styles.herosectionImageBox}
                />

                <Achievements
                    achievementData={useOlbAchievement()}
                    sectionTitle={t("servicePages:online-load-booking.achievements.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.achievements.sectionText")}
                />

                <ServiceInformation
                    heading={t("servicePages:online-load-booking.online-load-booking-benefits.heading")}
                    sectionTitle={t("servicePages:online-load-booking.online-load-booking-benefits.title")}
                    sectionText={t("servicePages:online-load-booking.online-load-booking-benefits.description")}
                />

                <ServiceInformation
                    heading={t("servicePages:online-load-booking.load-booking-available-in-cities.heading")}
                    sectionTitle={t("servicePages:online-load-booking.load-booking-available-in-cities.title")}
                    sectionText={t("servicePages:online-load-booking.load-booking-available-in-cities.description")}
                />

                <Features
                    features={useOlbFeatures()}
                    sectionTitle={t("servicePages:online-load-booking.features.sectionTitle")}
                    ctaLink={`${ROUTES.marketplace.getUrl("load")}`}
                    ctaLabel={t("servicePages:online-load-booking.features.ctaLabel")}
                    onCtaClick={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_load_booking, {
                            value: GA4ServicePageOnlineLoadBookingEventValues.start_booking_now
                        });
                    }}
                />

                <HowItWorks
                    howitwork={useOlbHowItworkData()}
                    sectionTitle={t("servicePages:online-load-booking.HowItWorks.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.HowItWorks.sectionText")}
                    tabAppNav={t("servicePages:online-load-booking.HowItWorks.tabAppNav")}
                    tabWebNav={t("servicePages:online-load-booking.HowItWorks.tabWebNav")}
                    onAppTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_load_booking, {
                            value: GA4ServicePageOnlineLoadBookingEventValues.mobile_app
                        });
                    }}
                    onWebTabChange={() => {
                        ga4sendEvent(GA4EventNames.Servicepage_online_load_booking, {
                            value: GA4ServicePageOnlineLoadBookingEventValues.web
                        });
                    }}
                />

                <TypesOfLoads
                    loadTypes={useLoadTypesData()}
                    sectionTitle={t("servicePages:online-load-booking.TypesOfLoads.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.TypesOfLoads.sectionText")}
                />

                <Faq
                    faqs={faqs}
                    sectionTitle={t("servicePages:online-load-booking.Faq.sectionTitle")}
                    sectionText={t("servicePages:online-load-booking.Faq.sectionText")}
                />
            </main>
        </>
    );
};

export default OnlineLoadBooking;
